import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../state/store';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { FlexBetween } from '../../components/FlexBetween';
import { Logger } from '../../utils/Logger';
import { ModuleOverviewPanel } from './ModuleOverviewPanel';



export const Dashboard: React.FC = () => {
  Logger.debug('Dashboard.tsx', 'Dashboard: Component is rendering');
  const theme = useTheme();
  const { hasInstantUpdateAccess } = useModulePermissions();
  const user = useSelector((state: RootState) => state.authentication.user);
  const company = useSelector((state: RootState) => state.authentication.company);
  const dashTheme = theme.palette.dashboard;
  const welcomeLeftColor = dashTheme?.welcomePanelLeftTextColor
    ?? (theme.palette.mode === 'light' ? 'black' : 'inherit');
  const welcomeRightColor = dashTheme?.welcomePanelRightTextColor
    ?? (theme.palette.mode === 'dark' ? 'black' : 'inherit');



  // Note: We no longer block access to the entire dashboard
  // Instead, we conditionally show InstantUpdate panels based on permissions



  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%" mb={3}>
      {/* Welcome Panel with User and Company License Details */}
      <Card sx={{ 
        mb: 4, 
        borderRadius: 4, 
        background: dashTheme?.welcomePanelGradient
          ?? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: dashTheme?.welcomePanelGradient ? 'inherit' : 'white',
      }}>
        <CardContent sx={{ p: 4 }}>
          <FlexBetween sx={{ flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 60%', minWidth: '300px' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon sx={{ 
                  mr: 2, 
                  fontSize: 40,
                  color: welcomeLeftColor,
                }} />
                <Box>
                  <Typography variant="h4" gutterBottom sx={{ 
                    fontWeight: 'bold',
                    color: welcomeLeftColor,
                  }}>
                    Welcome back, {user?.profile?.firstName || 'User'}!
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    opacity: 0.9,
                    color: welcomeLeftColor,
                  }}>
                    {hasInstantUpdateAccess() 
                      ? "Here's your InstantUpdate.ai dashboard overview"
                      : "Welcome to your dashboard"
                    }
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 35%', minWidth: '250px', textAlign: 'right' }}>
              <Box display="flex" alignItems="center" justifyContent="flex-end" mb={1}>
                <BusinessIcon sx={{ 
                  mr: 1,
                  color: welcomeRightColor,
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: welcomeRightColor,
                }}>
                  {company?.name || 'Company'}
                </Typography>
              </Box>
              {company?.productPlans && company.productPlans.length > 0 && (
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                  <VerifiedIcon sx={{ 
                    mr: 1, 
                    fontSize: 20,
                    color: welcomeRightColor,
                  }} />
                  <Typography variant="body2" sx={{ 
                    opacity: 0.9,
                    color: welcomeRightColor,
                  }}>
                    {company.productPlans.length} Active License{company.productPlans.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              )}
            </Box>
          </FlexBetween>
        </CardContent>
      </Card>

      <ModuleOverviewPanel />
    </Box>
  );
};
